import React, { useMemo } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { DataGridPro, GridColDef, GridColumnGroup, GridRenderCellParams } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { translate as t } from 'hooks/useTranslations';
import { neutral } from '_lex/_foundations/phxColors';
import type { FieldTypeEnum } from 'views/App/Portal/PerformanceReporting/FieldBank/_types/fieldbank';
import type { ReportDataRow, ReportRunResponse } from '../../_types';
import { transformReportData } from './transformReportData';

type ReportDataTableProps = {
  data: ReportRunResponse;
  selectedFieldIds: number[];
  allFieldLabels: Record<number, string>;
  isLoading: boolean;
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  onLoadMore?: () => void;
};

const NO_VALUE = '-';

const formatFieldValue = (value: string | number | boolean | null | undefined, type: FieldTypeEnum) => {
  if (value === null || value === undefined || value === '') {
    return NO_VALUE;
  }

  if (type === 'Boolean') {
    const boolVal = typeof value === 'string' ? value.toLowerCase() === 'true' : Boolean(value);
    return boolVal ? 'Yes' : 'No';
  }

  if (type === 'Date') {
    const d = dayjs(String(value));
    return d.isValid() ? d.format('MM/DD/YYYY') : NO_VALUE;
  }

  if (type === 'Time') {
    const parts = String(value).split(':');
    return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : NO_VALUE;
  }

  if (type === 'Number') {
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) return NO_VALUE;
    return new Intl.NumberFormat().format(numberValue);
  }

  if (type === 'Attachment' || type === 'VehicleImage' || type === 'BodyImage' || type === 'Narrative') {
    return String(value);
  }

  return String(value);
};

const TruncatedCell = ({ value }: { value: string }) => {
  if (value === NO_VALUE) return <span>{value}</span>;
  return (
    <Tooltip
      title={value}
      placement='top'
    >
      <Box
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
        }}
      >
        {value}
      </Box>
    </Tooltip>
  );
};

export default function ReportDataTable({
  data,
  selectedFieldIds,
  allFieldLabels,
  isLoading,
  isFetchingNextPage = false,
  hasNextPage = false,
  onLoadMore,
}: ReportDataTableProps) {
  const rows = useMemo(() => transformReportData(data), [data]);

  const fieldTypeById = useMemo(
    () =>
      data.templates.reduce<Record<number, FieldTypeEnum>>((acc, template) => {
        template.fields.forEach((field) => {
          acc[field.id] = field.type;
        });
        return acc;
      }, {}),
    [data.templates],
  );

  const fieldLabelById = useMemo(
    () =>
      data.templates.reduce<Record<number, string>>((acc, template) => {
        template.fields.forEach((field) => {
          acc[field.id] = field.label;
        });
        return acc;
      }, {}),
    [data.templates],
  );

  const { columns, columnGroupingModel } = useMemo(() => {
    const cols: GridColDef[] = [
      {
        field: 'caseNumber',
        headerName: t('reporting.performanceReportData.table.caseNumber'),
        width: 140,
        minWidth: 140,
        sortable: false,
        renderCell: (params: GridRenderCellParams<ReportDataRow, string | undefined>) => params.value ?? NO_VALUE,
      },
    ];

    const groups: GridColumnGroup[] = [];

    data.templates.forEach((template) => {
      const fieldIds = selectedFieldIds.length > 0
        ? selectedFieldIds
        : template.fields.map((field) => field.id);
      const children: { field: string }[] = [];

      fieldIds.forEach((fieldId) => {
        const colField = `t${template.id}_f${fieldId}`;
        const fieldType = fieldTypeById[fieldId] ?? 'Text';
        cols.push({
          field: colField,
          headerName: fieldLabelById[fieldId] ?? allFieldLabels[fieldId] ?? String(fieldId),
          minWidth: 180,
          flex: 1,
          sortable: false,
          renderCell: (params: GridRenderCellParams<ReportDataRow>) => (
            <TruncatedCell
              value={formatFieldValue(params.row[colField] as string | number | boolean | null | undefined, fieldType)}
            />
          ),
        });
        children.push({ field: colField });
      });

      if (children.length > 0) {
        groups.push({
          groupId: `template_${template.id}`,
          headerName: template.name,
          children,
        });
      }
    });

    return { columns: cols, columnGroupingModel: groups };
  }, [data.templates, selectedFieldIds, allFieldLabels, fieldLabelById, fieldTypeById]);

  const showColumnGroups = data.templates.length > 0;

  if (!rows.length && !isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 6,
          border: `1px solid ${neutral[200]}`,
          borderRadius: 2,
          backgroundColor: neutral[50],
        }}
      >
        <Typography variant='body2'>{t('reporting.performanceReportData.table.noResults')}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: 0,
        height: '100%',
        border: `1px solid ${neutral[200]}`,
        borderRadius: 2,
        overflow: 'hidden',
        '& .MuiDataGrid-cell': {
          borderColor: neutral[200],
          display: 'flex',
          alignItems: 'center',
        },
        '& .MuiDataGrid-columnHeaders': {
          borderColor: neutral[200],
          backgroundColor: neutral[50],
        },
        '& .MuiDataGrid-columnHeader--filledGroup': {
          backgroundColor: neutral[50],
          color: neutral[500],
          textTransform: 'uppercase',
          fontSize: '0.75rem',
          fontWeight: 600,
          letterSpacing: '0.08em',
          borderRight: `1px solid ${neutral[200]}`,
        },
        '& .MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitleContainer': {
          justifyContent: 'center',
        },
      }}
    >
      <DataGridPro
        rows={rows}
        columns={columns}
        loading={isLoading || isFetchingNextPage}
        getRowId={(row: ReportDataRow) => row.id}
        rowSelectionModel={[]}
        disableColumnFilter
        disableColumnMenu
        hideFooterPagination
        columnGroupHeaderHeight={44}
        columnHeaderHeight={40}
        rowHeight={56}
        sortingOrder={[]}
        pinnedColumns={{ left: ['caseNumber'] }}
        columnGroupingModel={showColumnGroups ? columnGroupingModel : undefined}
        onRowsScrollEnd={() => {
          if (hasNextPage && onLoadMore) {
            onLoadMore();
          }
        }}
      />
    </Box>
  );
}
